import { useParams, Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Gauge, ClipboardCheck, AlertTriangle, ShieldAlert, UserRound, UserCheck } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { PageHeader, Kpi, Panel, Loading, Empty, Plate, VehicleStatus, AnomalyBadge, LevelBadge, DemoTag, useFetch } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChecksTable } from "@/pages/livraison/LivDashboard";
import { useAuth } from "@/context/AuthContext";
import { fNum, fDate } from "@/lib/api";
import { RemindersTable, HistoryTable } from "@/components/Maintenance";
import EtatDesLieux from "@/pages/livraison/EtatDesLieux";
import { AccidentsTable } from "@/pages/livraison/Accidents";
import { VehicleDocuments } from "@/components/Documents";
import { AdBlueDialog, AdBlueHistory, AdBlueToggle } from "@/components/AdBlue";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Droplets } from "lucide-react";

export function VehicleMaintenance({ vehicleId }) {
  const rem = useFetch(vehicleId ? "/maintenance/reminders" : null, { vehicle_id: vehicleId });
  const hist = useFetch(vehicleId ? "/maintenance" : null, { vehicle_id: vehicleId });
  return (
    <div data-testid="vehicle-maintenance">
      <div className="px-4 pt-3 text-[11px] uppercase font-bold tracking-wider text-slate-500">Rappels</div>
      <RemindersTable rows={rem.data || []} showPlate={false} />
      <div className="px-4 pt-4 text-[11px] uppercase font-bold tracking-wider text-slate-500">Historique</div>
      <HistoryTable rows={hist.data || []} showPlate={false} />
    </div>
  );
}

export function PeriodsTable({ rows, label, link, testid }) {
  if (!rows.length) return <Empty text="Aucun historique (aucun contrôle avec chauffeur attribué)" />;
  return (
    <table className="tbl w-full" data-testid={testid}>
      <thead><tr><th>{label}</th><th>Du</th><th>Au</th><th className="text-right">Contrôles</th></tr></thead>
      <tbody>{rows.map((p, i) => (
        <tr key={i}><td className="font-semibold">{link ? <Link to={link(p.key)} className="hover:underline">{p.label}</Link> : p.label}</td>
          <td>{fDate(p.start)}</td><td>{fDate(p.end)}</td><td className="text-right tabular-nums">{p.count}</td></tr>
      ))}</tbody>
    </table>
  );
}

export function AnomalyTable({ items }) {
  if (!items.length) return <Empty text="Aucune anomalie" />;
  return (
    <div className="overflow-x-auto">
      <table className="tbl w-full">
        <thead><tr><th>Date</th><th>Nature</th><th>Description</th><th>Chauffeur attribué</th><th>Contrôlé par</th><th>Statut</th><th>Intervention</th><th>Résolution</th></tr></thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} data-testid={`fiche-anomaly-${a.id}`}>
              <td>{fDate(a.date)}</td><td className="font-semibold">{a.nature}</td>
              <td className="max-w-[240px] truncate">{a.description || "—"}</td><td className="font-semibold">{a.driver_name}</td><td className="text-slate-500">{a.controller_name}</td>
              <td><AnomalyBadge v={a.status} /></td><td>{fDate(a.intervention_date)}</td>
              <td className="max-w-[200px] truncate text-slate-500">{a.resolution_note || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function VehicleFiche() {
  const { id } = useParams();
  const { user } = useAuth();
  const { data, loading, error, reload } = useFetch(`/livraison/vehicles/${id}/fiche`);
  const veh = useFetch(`/vehicles/${id}`);
  const acc = useFetch("/accidents", { vehicle_id: id });
  const [sp] = useSearchParams();
  const [adb, setAdb] = useState(false);
  const [ak, setAk] = useState(0);
  if (loading) return <Loading />;
  if (!data) return <Empty text={error || "Véhicule introuvable"} />;
  const v = data.vehicle;
  const admin = user.role === "admin";
  return (
    <div data-testid="vehicle-fiche">
      <Link to={v.status === "archive" ? "/parametres/vehicules" : "/livraison/vehicules"} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0E2D43] mb-4" data-testid="back-to-vehicles">
        <ArrowLeft className="w-4 h-4" /> Véhicules
      </Link>
      <PageHeader overline={`${v.engine} · mise en circulation ${fDate(v.first_registration)}`} title={`${v.brand} ${v.model}`}>
        <div className="flex flex-wrap items-center gap-2"><Plate>{v.plate}</Plate><VehicleStatus v={v.status} /><DemoTag show={v.is_demo} />
          {v.uses_adblue && user.real_role !== "direction" && v.status !== "archive" && <Button size="sm" onClick={() => setAdb(true)} className="bg-[#2F80ED] hover:bg-[#1d6fd8]" data-testid="fiche-add-adblue"><Droplets className="w-4 h-4 mr-1" /> Ajouter de l'AdBlue</Button>}
          <AdBlueToggle vehicle={v} onDone={reload} />
        </div>
      </PageHeader>
      {adb && <AdBlueDialog vehicleId={id} onClose={() => setAdb(false)} onDone={() => { setAdb(false); setAk((x) => x + 1); reload(); }} />}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-4">
        <Kpi testid="fiche-last-driver" label="Dernier chauffeur attribué" value={data.last_assigned?.driver_name || "—"} sub={data.last_assigned ? `Le ${fDate(data.last_assigned.date)}` : "Non renseigné"} icon={UserRound} tone="accent" />
        <Kpi testid="fiche-last-check-date" label="Dernier contrôle" value={data.last_check ? fDate(data.last_check.date) : "—"} icon={ClipboardCheck} tone="navy" delay={1} />
        <Kpi testid="fiche-last-controller" label="Contrôlé par" value={data.last_check?.controller_name || "—"} icon={UserCheck} tone="navy" delay={2} />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <Kpi testid="fiche-last-mileage" label="Dernier kilométrage" value={`${fNum(data.last_mileage)} km`} sub={data.last_mileage_source || (data.last_check ? `Contrôle du ${fDate(data.last_check.date)}` : "Aucun contrôle")} icon={Gauge} tone="navy" />
        <Kpi testid="fiche-checks-count" label="Contrôles" value={data.checks.length} icon={ClipboardCheck} delay={1} />
        <Kpi testid="fiche-open-anomalies" label="Anomalies actuelles" value={data.current_anomalies.length} icon={AlertTriangle} tone="danger" delay={2} />
        <Kpi testid="fiche-damages" label="Dommages signalés" value={data.damages.length} icon={ShieldAlert} delay={3} />
      </div>
      <Panel title="Évolution du kilométrage" className="mb-6" testid="fiche-mileage-chart">
        {data.mileage_series.length < 2 ? <Empty text="Pas assez de relevés" /> : (
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={data.mileage_series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fDate} tick={{ fontSize: 11 }} minTickGap={30} />
                <YAxis tick={{ fontSize: 11 }} domain={["dataMin", "dataMax"]} tickFormatter={(x) => fNum(x)} width={70} />
                <Tooltip labelFormatter={fDate} formatter={(x) => `${fNum(x)} km`} />
                <Line dataKey="mileage" stroke="#F49619" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Panel>
      <Tabs defaultValue={sp.get("tab") || "checks"}>
        <TabsList className="flex-wrap h-auto mb-3">
          <TabsTrigger value="edl" data-testid="tab-edl">État des lieux</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="accidents" data-testid="tab-accidents">Accidents ({acc.data?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="checks" data-testid="tab-checks">Historique des contrôles</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="tab-drivers">Historique des chauffeurs</TabsTrigger>
          <TabsTrigger value="current" data-testid="tab-current">Anomalies actuelles</TabsTrigger>
          {admin && <TabsTrigger value="past" data-testid="tab-past">Anomalies passées</TabsTrigger>}
          <TabsTrigger value="damages" data-testid="tab-damages">Dommages</TabsTrigger>
          {admin && <TabsTrigger value="interventions" data-testid="tab-interventions">Interventions</TabsTrigger>}
          <TabsTrigger value="assign" data-testid="tab-assignments">Affectations</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">Entretiens</TabsTrigger>
          {v.uses_adblue && <TabsTrigger value="adblue" data-testid="tab-adblue">Historique AdBlue</TabsTrigger>}
        </TabsList>
        <Panel flush>
          <TabsContent value="checks" className="m-0"><ChecksTable checks={data.checks} showPlate={false} /></TabsContent>
          <TabsContent value="docs" className="m-0"><VehicleDocuments vehicleId={id} canEdit={user.canEdit || user.role === "chauffeur"} canDelete={user.canEdit} /></TabsContent>
          <TabsContent value="edl" className="m-0"><EtatDesLieux vehicleId={id} archived={v.status === "archive"} /></TabsContent>
          <TabsContent value="accidents" className="m-0"><AccidentsTable rows={acc.data || []} admin={admin} showVehicle={false} /></TabsContent>
          <TabsContent value="drivers" className="m-0"><PeriodsTable rows={data.driver_periods} label="Chauffeur" testid="fiche-driver-periods" link={admin ? (k) => `/livraison/chauffeurs/${k}` : null} /></TabsContent>
          <TabsContent value="current" className="m-0"><AnomalyTable items={data.current_anomalies} /></TabsContent>
          <TabsContent value="past" className="m-0"><AnomalyTable items={data.past_anomalies} /></TabsContent>
          <TabsContent value="interventions" className="m-0"><AnomalyTable items={data.interventions} /></TabsContent>
          <TabsContent value="damages" className="m-0">
            {!data.damages.length ? <Empty text="Aucun dommage signalé" /> : (
              <table className="tbl w-full"><thead><tr><th>Date</th><th>Niveau</th><th>Chauffeur attribué</th><th>Contrôlé par</th><th>Commentaire</th></tr></thead>
                <tbody>{data.damages.map((c) => (
                  <tr key={c.id}><td>{fDate(c.date)}</td><td><LevelBadge v={c.dommages} /></td><td>{c.driver_name}</td><td>{c.controller_name}</td><td className="whitespace-normal">{c.comment || "—"}</td></tr>
                ))}</tbody></table>
            )}
          </TabsContent>
          <TabsContent value="maintenance" className="m-0"><VehicleMaintenance vehicleId={id} /></TabsContent>
          <TabsContent value="adblue" className="m-0"><AdBlueHistory vehicleId={id} k={ak} /></TabsContent>
          <TabsContent value="assign" className="m-0">
            {!veh.data?.assignments?.length ? <Empty text="Aucune affectation" /> : (
              <table className="tbl w-full"><thead><tr><th>Utilisateur</th><th>Début</th><th>Fin</th></tr></thead>
                <tbody>{veh.data.assignments.map((a) => (
                  <tr key={a.id}><td className="font-semibold">{a.user_name}</td><td>{fDate(a.start_date)}</td><td>{a.end_date ? fDate(a.end_date) : "En cours"}</td></tr>
                ))}</tbody></table>
            )}
          </TabsContent>
        </Panel>
      </Tabs>
    </div>
  );
}
